package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable

/**
 * Represents a user-configurable contract type.
 * This is the object the business owner creates and manages.
 */
@Serializable
data class ContractType(
    val id: String,
    val label: String, // e.g., "Student (Zlecenie)", "Full-Time Salaried"
    val systemType: SystemContractType,
    val countryCode: String = "PL" // Default, can be expanded later
)

/**
 * A fixed, non-configurable enum that the backend/domain logic uses for calculations.
 * This ensures that financial logic is stable, regardless of what the user names their contracts.
 * Polish names: STANDARD_EMPLOYMENT = umowa o pracę (UoP), CIVIL_LAW_CONTRACT = umowa zlecenie,
 * SPECIFIC_TASK_CONTRACT = umowa o dzieło, B2B_CONTRACTOR = własna działalność gospodarcza (B2B,
 * invoicing), OWNER_PARTNER = the owner or a partner working without any contract (SH-133: no
 * labour rules, no contributions, labour cost 0 unless a notional hourly amount is set).
 */
@Serializable
enum class SystemContractType(val isEmployee: Boolean) {
    STANDARD_EMPLOYMENT(isEmployee = true),
    CIVIL_LAW_CONTRACT(isEmployee = true),
    SPECIFIC_TASK_CONTRACT(isEmployee = false),
    B2B_CONTRACTOR(isEmployee = false),
    OWNER_PARTNER(isEmployee = false),
    NOT_SET(isEmployee = false);

    companion object {
        private val ALIASES = mapOf(
            "uop" to STANDARD_EMPLOYMENT, "umowa o pracę" to STANDARD_EMPLOYMENT, "employment" to STANDARD_EMPLOYMENT,
            "zlecenie" to CIVIL_LAW_CONTRACT, "umowa zlecenie" to CIVIL_LAW_CONTRACT, "uz" to CIVIL_LAW_CONTRACT,
            "dzieło" to SPECIFIC_TASK_CONTRACT, "dzielo" to SPECIFIC_TASK_CONTRACT, "uod" to SPECIFIC_TASK_CONTRACT,
            "umowa o dzieło" to SPECIFIC_TASK_CONTRACT,
            "b2b" to B2B_CONTRACTOR,
            "owner" to OWNER_PARTNER, "partner" to OWNER_PARTNER,
            "właściciel" to OWNER_PARTNER, "wspólnik" to OWNER_PARTNER
        )

        /** The stored id: an enum name, or the free text older documents hold ("UoP", "Zlecenie", "B2B"). */
        fun fromId(id: String?): SystemContractType {
            val key = id?.trim().orEmpty()
            if (key.isEmpty()) return NOT_SET
            return entries.firstOrNull { it.name.equals(key, true) } ?: ALIASES[key.lowercase()] ?: NOT_SET
        }
    }
}
